import {Injectable} from '@angular/core';
import {BehaviorSubject,defer,Observable} from "rxjs";
import {Result} from "@domain/common/http/result";
import {environment} from "@environments/environment";
import {KeyStatutApplication} from "@domain/admin/statut-application/statut-application";

/**
 * Service de gestion du statut public de l'application
 */
@Injectable()
export class StatutApplicationUserService {
	/**
	 * Constante pour la durée (ms) de temporisation avant mise en maintenance forcée.
	 * À garder synchronisé avec la variable TOTAL_TEMPO_MAINTENANCE côté back (modulo l'unité).
	 * Mettre à 0 pour désactiver le polling de rafraichissement du statut lorsque l'application est en ligne.
	 */
	static readonly TOTAL_TEMPO_MAINTENANCE: number = 0;

	/**
	 * Constante pour la durée (ms) de temporisation avant déclenchement du calcul WF.
	 * À garder synchronisé avec la variable TOTAL_TEMPO_WF côté back (modulo l'unité).
	 */
	static readonly TOTAL_TEMPO_WF: number = 300000;

	/** Durée (ms) avant le prochain appel du polling de rafraichissement du statut (veille) */
	static readonly TEMPO_VEILLE: number = 120000;

	/** Durée (ms) avant le prochain appel du polling de rafraichissement du statut (durant la temporisation (si activée)) */
	static readonly TEMPO_MAINTENANCE: number = 10000;

	/** Durée (ms) avant prochain rafraichissement local de la progression */
	static readonly TEMPO_PROGRESSION_LOCALE: number = 100;

	/** Flag d'activité' */
	private isRefreshing: boolean = false;

	/** Flag de verrouillage du rafraichissement */
	private isRefreshLocked: boolean = false;

	/** Statut de l'application */
	private _statut: BehaviorSubject<KeyStatutApplication> = new BehaviorSubject<KeyStatutApplication>(null);
	get statut$() { return this._statut.asObservable(); }

	/** Progression de la temporisation */
	private _progression: BehaviorSubject<number> = new BehaviorSubject<number>(0.00);
	get progression$() { return this._progression.asObservable(); }

	/** Message de maintenance administrateur */
	private _messageMaintenance: BehaviorSubject<string> = new BehaviorSubject<string>(null);
	get messageMaintenance$() { return this._messageMaintenance.asObservable(); }

	/** ID de l'intervalle */
	private intervalId: NodeJS.Timeout;

	/** Référence de la prochaine exécution du cycle de rafraichissement du statut */
	private timeoutId: NodeJS.Timeout;

	/** Temps (ms) restant avant écoulement de la temporisation */
	private millisecondsToTrigger: number;

	/**
	 * Constructeur
	 */
	constructor() {}

	/**
	 * Récupération du statut public de l'application
	 */
	private getPublicApplicationStatus(): Observable<Result> {
		//Utilisation de la fonction fetch qui permet de préciser 'credentials: "omit"' afin de ne pas envoyer les cookies de la session courante.
		//Dans le cas contraire le cookie contenant le nom de la session Tomcat est envoyé et la session côté serveur est réactualisée à chaque appel,
		//ce qui empêche l'utilisateur d'être déconnecté après 1h d'inactivité (suivant le paramètre actuellement défini dans le fichier web.xml)
		//L'appel est ensuite "wrapé" avec la fonction "defer" pour créer un "cold observable" qui ne se déclenchera que lors de l'abonnement (comme lors d'un appel http classique fait avec HttpClient)
		return defer(() => fetch(`${environment.baseUrl}/controller/StatutApplication/getPublicApplicationStatus`, {
			method: "GET",
			credentials: "omit",
		}).then((response: Response) => response.json()));
	}

	/**
	 * Lancement de la surveillance du statut de l'application
	 *
	 * @param isForced true si forçage du rafraichissement
	 */
	checkPublicApplicationStatus(isForced = false): void {
		//Si la surveillance du statut de l'application n'est pas déjà en cours
		if (!this.isRefreshing || isForced) {
			//Bascule du flag de surveillance activée
			this.isRefreshing = true;

			//Abonnement à la mise à jour du statut de l'application
			this.getPublicApplicationStatus().subscribe((result) => this.handleStatus(result,isForced),(error) => {});
		}
	}

	/**
	 * Gestion du statut retourné
	 *
	 * @param result résultat retourné
	 * @param isForced Permet de mettre à jour le statut une seule fois sans (re)lancer la surveillance même si le verrouillage du rafraichissement est désactivé
	 */
	private handleStatus(result: Result,isForced: boolean = false): void {
		//Emission du statut de l'application si nécessaire
		this._statut.getValue() != result?.data?.applicationStatut && this._statut.next(result?.data?.applicationStatut as KeyStatutApplication);

		//Reset de l'intervalle précédent
		clearInterval(this.intervalId);

		//Si l'application est hors ligne ou en attente
		if (result?.data?.applicationStatut == KeyStatutApplication.HORS_LIGNE || result?.data?.applicationStatut == KeyStatutApplication.HORS_LIGNE_TEMPO) {
			//Emission du message de maintenance
			this._messageMaintenance.next(result?.data?.messageMaintenance);

			//Si l'application est en attente de hors ligne
			if (result?.data?.applicationStatut == KeyStatutApplication.HORS_LIGNE_TEMPO) {
				//Sauvegarde du temps restant
				this.millisecondsToTrigger = result?.data?.millisecondsToTrigger;

				//Définition d'un intervalle de progression pour faire évoluer la barre en attendant le prochain appel
				this.intervalId = setInterval(() => {
					//Incrément local du temps restant
					this.millisecondsToTrigger -= StatutApplicationUserService.TEMPO_PROGRESSION_LOCALE;

					//Calcul et émission du pourcentage d'avancement de la temporisation
					this._progression.next((StatutApplicationUserService.TOTAL_TEMPO_MAINTENANCE - this.millisecondsToTrigger) * 100 / StatutApplicationUserService.TOTAL_TEMPO_MAINTENANCE);
				}, StatutApplicationUserService.TEMPO_PROGRESSION_LOCALE);

				//Calcul et émission du pourcentage d'avancement de la temporisation
				this._progression.next((StatutApplicationUserService.TOTAL_TEMPO_MAINTENANCE - result?.data?.millisecondsToTrigger) * 100 / StatutApplicationUserService.TOTAL_TEMPO_MAINTENANCE);
			}
		}

		//Si le rafraichissement est autorisé
		if (!this.isRefreshLocked && !isForced) {
			//Annulation d'un éventuel cycle de surveillance déjà en cours
			if (this.timeoutId != null) {
				clearTimeout(this.timeoutId);
			}

			//Gestion de la fréquence de vérification en fonction du statut de l'application
			this.timeoutId = setTimeout(() => this.getPublicApplicationStatus()
				.subscribe(
					(result) => this.handleStatus(result),
					(error) => {}
				), result?.data?.applicationStatut == KeyStatutApplication.HORS_LIGNE_TEMPO ? StatutApplicationUserService.TEMPO_MAINTENANCE : StatutApplicationUserService.TEMPO_VEILLE);
		}
	}

	/**
	 * Verrouillage du rafraichissement de statut
	 */
	lockRefresh(): void {
		//Bascule du flag de blocage
		this.isRefreshLocked = true;

		//Bascule du flag de rafraichissement en cours
		this.isRefreshing = false;
	}

	/**
	 * Déverrouillage du rafraichissement de statut
	 */
	unlockRefresh(): void {
		//Bascule du flag de blocage
		this.isRefreshLocked = false;
	}

	/**
	 * Vérifie s'il y a une temporisation avant la mise en maintenance forcée
	 */
	isPollingEnabled(): boolean {
		return StatutApplicationUserService.TOTAL_TEMPO_MAINTENANCE > 0;
	}
}
